import type React from "react";
import { useEffect, useState, useRef, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Trash2, Download, X, Plus, Truck, Ship, Plane } from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getShipments,
  deleteMultipleShipments,
  deleteShipment,
  clearError,
  getShipmentCopyData,
  getShipmentColumns,
  setColumns,
  setLoading,
} from "../../store/slices/shipmentSlice";
import type { Shipment, PartnerSuggestion, ShipmentFilters } from "../../types";
import ShipmentTable from "../../components/shipments/ShipmentTable";
import { Pagination } from "../../components/ListView/Pagination";
import { ShipmentFiltersComponent as FiltersComponent } from "../../components/shipments/ShipmentFilters";
import { useShipmentExport } from "../../hooks/useShipmentExport";
import ColumnConfigDropdown from "../../components/shipments/ColumnConfigDropdown";
import { useTranslation } from "react-i18next";

// ─── Add Shipment Dropdown ────────────────────────────────────────────────────
const AddShipmentDropdown: React.FC = () => {
  const { t } = useTranslation("shipments");
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  const handleSelectMode = (mode: "ROAD" | "SEA" | "AIR") => {
    setIsOpen(false);
    navigate(`/shipments/new?transportMode=${mode}`);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const modeOptions = [
    {
      mode: "ROAD" as const,
      icon: Truck,
      title: t("transportMode.road"),
      description: t("list.roadDesc"),
      iconBg: "bg-green-100 group-hover:bg-green-200",
      iconColor: "text-green-600",
    },
    {
      mode: "SEA" as const,
      icon: Ship,
      title: t("transportMode.sea"),
      description: t("list.seaDesc"),
      iconBg: "bg-blue-100 group-hover:bg-blue-200",
      iconColor: "text-blue-600",
    },
    {
      mode: "AIR" as const,
      icon: Plane,
      title: t("transportMode.air"),
      description: t("list.airDesc"),
      iconBg: "bg-sky-100 group-hover:bg-sky-200",
      iconColor: "text-sky-600",
    },
  ];

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        className="inline-flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
      >
        <Plus className="h-4 w-4" />
        <span>{t("list.addShipment")}</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 overflow-hidden">
          <div className="py-1">
            {modeOptions.map((option, index) => {
              const Icon = option.icon;
              return (
                <Fragment key={option.mode}>
                  {index > 0 && <div className="border-t border-gray-100" />}
                  <button
                    type="button"
                    onClick={() => handleSelectMode(option.mode)}
                    className="w-full flex items-center px-4 py-3 text-left hover:bg-gray-50 transition-colors group"
                  >
                    <div
                      className={`shrink-0 w-10 h-10 ${option.iconBg} rounded-lg flex items-center justify-center mr-3 transition-colors`}
                    >
                      <Icon className={`h-5 w-5 ${option.iconColor}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-dark-900">
                        {option.title}
                      </p>
                      <p className="text-xs text-dark-500">
                        {option.description}
                      </p>
                    </div>
                  </button>
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export const ShipmentsList: React.FC = () => {
  const { t } = useTranslation("shipments");
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    shipments,
    loading,
    error,
    columns: reduxColumns,
    pagination: { total, page, pages },
  } = useAppSelector((state) => state.shipment);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedDirections, setSelectedDirections] = useState<string[]>([]);
  const [selectedPartners, setSelectedPartners] = useState<PartnerSuggestion[]>(
    [],
  );
  const [selectedAddresses, setSelectedAddresses] = useState<
    PartnerSuggestion[]
  >([]);
  const [truckCarrier, setTruckCarrier] = useState<string[]>([]);
  const [trailerCarrier, setTrailerCarrier] = useState<string[]>([]);
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");

  // Route-based filters (free-text, matched against pickup/delivery companyName & city)
  const [pickupLocation, setPickupLocation] = useState("");
  const [deliveryLocation, setDeliveryLocation] = useState("");

  // Bumped whenever an action removes rows. Deleting patches the slice's array
  // in place, which leaves the page short and the totals — the pagination
  // footer, the status chips — describing a list that no longer exists.
  // Refetching is the only way to get all of those right again.
  const [listRefreshToken, setListRefreshToken] = useState(0);

  // ── Column config ─────────────────────────────────────────────────────────
  const [isColumnConfigOpen, setIsColumnConfigOpen] = useState(false);
  const [columnsLoaded, setColumnsLoaded] = useState(false);
  const [isServiceIncluded, setIsServiceIncluded] = useState<boolean>(false);

  const [filters, setFilters] = useState<ShipmentFilters>({
    page: 1,
    limit: 20,
    search: "",
  });

  const prevSearch = useRef(filters.search);

  const { handleExportReport } = useShipmentExport({
    filters,
    selectedStatuses,
    selectedDirections,
    selectedPartners,
    selectedAddresses,
    truckCarrier,
    trailerCarrier,
    dateRangeStart,
    dateRangeEnd,
    pickupLocation,
    deliveryLocation,
    isServiceIncluded,
  });

  // ── Navigation state (open from notification) ─────────────────────────────
  // Reclamation notifications land here with the shipment to expand in the
  // side panel; every other shipment notification opens the shipment form.
  const [focusReclamation, setFocusReclamation] = useState<{
    _id: string;
    shipmentNumber: string;
  } | null>(null);

  useEffect(() => {
    const state = location.state as {
      openShipmentId?: string;
      openReclamationShipmentId?: string;
      openReclamationShipmentNumber?: string;
      timestamp?: number;
    };
    if (state?.openReclamationShipmentId) {
      window.history.replaceState({}, document.title);
      setFocusReclamation({
        _id: state.openReclamationShipmentId,
        shipmentNumber: state.openReclamationShipmentNumber ?? "",
      });
      return;
    }
    if (state?.openShipmentId) {
      window.history.replaceState({}, document.title);
      navigate(`/shipments/${state.openShipmentId}`);
    }
  }, [location.state, navigate]);

  // ── Load columns once ─────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(setLoading());
    const loadColumns = async () => {
      try {
        await dispatch(getShipmentColumns()).unwrap();
      } finally {
        setColumnsLoaded(true);
      }
    };
    loadColumns();
  }, [dispatch]);

  // ── Fetch shipments whenever filters change ───────────────────────────────
  useEffect(() => {
    if (!columnsLoaded) return;

    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search || "";

    const params: any = {
      page: filters.page,
      limit: filters.limit,
    };

    if (filters.search) params.search = filters.search;
    if (selectedStatuses.length > 0) params.statuses = selectedStatuses;
    if (selectedDirections.length > 0) params.directions = selectedDirections;
    if (dateRangeStart) params.dateFrom = dateRangeStart;
    if (dateRangeEnd) params.dateTo = dateRangeEnd;

    // Pickup location: passed as pickupLocation param (backend filters by pickup.companyName / pickup.city)
    if (pickupLocation.trim()) params.pickupLocation = pickupLocation.trim();
    if (deliveryLocation.trim())
      params.deliveryLocation = deliveryLocation.trim();

    if (selectedPartners.length > 0) {
      params.companies = selectedPartners.map((c) => c.companyName.trim());
    }
    if (selectedAddresses.length > 0) {
      params.companies = [
        ...(params.companies || []),
        ...selectedAddresses.map((c) => c.companyName.trim()),
      ];
    }

    if (truckCarrier.length > 0) {
      params.vehicles = [
        ...new Set([...(params.vehicles || []), ...truckCarrier]),
      ];
    }
    if (trailerCarrier.length > 0) {
      params.vehicles = [
        ...new Set([...(params.vehicles || []), ...trailerCarrier]),
      ];
    }

    if (searchChanged) {
      if (filters.search === "") {
        dispatch(getShipments(params));
      } else {
        const t = setTimeout(() => dispatch(getShipments(params)), 750);
        return () => clearTimeout(t);
      }
      return;
    }

    dispatch(getShipments(params));
  }, [
    dispatch,
    filters,
    selectedStatuses,
    selectedDirections,
    selectedPartners,
    selectedAddresses,
    truckCarrier,
    trailerCarrier,
    dateRangeStart,
    dateRangeEnd,
    pickupLocation,
    deliveryLocation,
    columnsLoaded,
    listRefreshToken,
  ]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Deleting the last rows of the last page strands the user on a page that no
  // longer exists. Step back to the final real page instead of showing an empty
  // table under a pager that claims there is more.
  useEffect(() => {
    if (loading) return;
    if (pages > 0 && page > pages) {
      setFilters((prev) => ({ ...prev, page: pages }));
    }
  }, [loading, page, pages]);

  // ── Filter handlers ───────────────────────────────────────────────────────
  const handleFiltersChange = (newFilters: Partial<ShipmentFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page || 1,
    }));
    setSelectedIds([]);
  };

  const handlePageChange = (page: number) => handleFiltersChange({ page });

  const handleStatusFilter = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleDirectionFilter = (direction: string) => {
    setSelectedDirections((prev) =>
      prev.includes(direction)
        ? prev.filter((d) => d !== direction)
        : [...prev, direction],
    );
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handlePickupLocationChange = (value: string) => {
    setPickupLocation(value);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleDeliveryLocationChange = (value: string) => {
    setDeliveryLocation(value);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handlePartnerSelect = (company: PartnerSuggestion) => {
    setSelectedPartners((prev) => [...prev, company]);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handlePartnerRemove = (company: string) => {
    setSelectedPartners((prev) => prev.filter((c) => c._id !== company));
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleAddressSelect = (company: PartnerSuggestion) => {
    setSelectedAddresses((prev) => [...prev, company]);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleAddressRemove = (company: string) => {
    setSelectedAddresses((prev) => prev.filter((c) => c._id !== company));
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleTruckSelect = (carrier: string) => {
    setTruckCarrier((prev: any) => [...prev, carrier]);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleTruckRemove = (carrier: string) => {
    setTruckCarrier((prev: any) => prev.filter((v: any) => v !== carrier));
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleTrailerSelect = (carrier: string) => {
    setTrailerCarrier((prev: any) => [...prev, carrier]);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleTrailerRemove = (carrier: string) => {
    setTrailerCarrier((prev: any) => prev.filter((v: any) => v !== carrier));
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleDateRangeChange = (start: string, end: string) => {
    setDateRangeStart(start);
    setDateRangeEnd(end);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const handleDateRangeClear = () => {
    setDateRangeStart("");
    setDateRangeEnd("");
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSelectedStatuses([]);
    setSelectedDirections([]);
    setSelectedPartners([]);
    setSelectedAddresses([]);
    setTrailerCarrier([]);
    setTruckCarrier([]);
    setDateRangeStart("");
    setDateRangeEnd("");
    setPickupLocation("");
    setDeliveryLocation("");
    setFilters({ page: 1, limit: 20, search: "" });
    setIsServiceIncluded(false);
  };

  // ── Selection handlers ────────────────────────────────────────────────────
  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? shipments.map((s) => s._id) : []);
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((selectedId) => selectedId !== id),
    );
  };

  const handleCopyShipment = async (id: string) => {
    try {
      const copyData = await dispatch(getShipmentCopyData(id)).unwrap();
      navigate("/shipments/new", { state: { copyData } });
    } catch {
      toast.error(t("list.copyFailed"));
    }
  };

  const handleEdit = (shipment: Shipment) =>
    navigate(`/shipments/${shipment._id}`);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("list.confirmDelete")))
      return;
    try {
      await dispatch(deleteShipment(id)).unwrap();
      toast.success(t("list.deletedSuccess"));
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
      setListRefreshToken((n) => n + 1);
    } catch {}
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(
        t("list.confirmDeleteMultiple", { count: selectedIds.length }),
      )
    )
      return;
    try {
      await dispatch(deleteMultipleShipments(selectedIds)).unwrap();
      toast.success(t("list.deletedMultiple", { count: selectedIds.length }));
      setSelectedIds([]);
      setListRefreshToken((n) => n + 1);
    } catch {}
  };

  const handleSetColumns = (newColumns: any) =>
    dispatch(setColumns(newColumns));

  // ── Active filter chips helpers ───────────────────────────────────────────
  const hasChips =
    selectedPartners.length > 0 ||
    selectedAddresses.length > 0 ||
    truckCarrier.length > 0 ||
    trailerCarrier.length > 0 ||
    dateRangeStart ||
    dateRangeEnd ||
    selectedDirections.length > 0 ||
    pickupLocation ||
    deliveryLocation;

  const pagination = {
    page,
    pages,
    total,
    hasNext: page < pages,
    hasPrev: page > 1,
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="px-6 py-4">
        {/* Header row */}
        <div className="flex justify-between items-center mb-6 flex-wrap max-sm:gap-y-3">
          <h1 className="text-3xl font-bold text-dark-900">{t("page.title")}</h1>
          <div className="flex items-center flex-wrap space-x-3 max-sm:gap-y-3">
            <ColumnConfigDropdown
              columns={reduxColumns}
              setColumns={handleSetColumns}
              isColumnConfigOpen={isColumnConfigOpen}
              setIsColumnConfigOpen={setIsColumnConfigOpen}
            />
            <FiltersComponent
              filters={filters}
              selectedStatuses={selectedStatuses}
              selectedDirections={selectedDirections}
              selectedPartners={selectedPartners}
              selectedAddresses={selectedAddresses}
              truckCarrier={truckCarrier}
              trailerCarrier={trailerCarrier}
              isServiceIncluded={isServiceIncluded}
              onServiceToggle={setIsServiceIncluded}
              dateRangeStart={dateRangeStart}
              dateRangeEnd={dateRangeEnd}
              pickupLocation={pickupLocation}
              deliveryLocation={deliveryLocation}
              onFiltersChange={handleFiltersChange}
              onStatusFilter={handleStatusFilter}
              onDirectionFilter={handleDirectionFilter}
              onPickupLocationChange={handlePickupLocationChange}
              onDeliveryLocationChange={handleDeliveryLocationChange}
              onPartnerSelect={handlePartnerSelect}
              onPartnerRemove={handlePartnerRemove}
              onAddressSelect={handleAddressSelect}
              onAddressRemove={handleAddressRemove}
              onTruckSelect={handleTruckSelect}
              onTruckRemove={handleTruckRemove}
              onTrailerSelect={handleTrailerSelect}
              onTrailerRemove={handleTrailerRemove}
              onDateRangeChange={handleDateRangeChange}
              onDateRangeClear={handleDateRangeClear}
              onClearFilters={clearFilters}
            />
            <button
              onClick={handleExportReport}
              className="inline-flex items-center space-x-2 px-4 py-2 border border-dark-300 rounded-lg hover:bg-dark-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>{t("list.export")}</span>
            </button>
            <AddShipmentDropdown />
          </div>
        </div>

        {/* Active filter chips */}
        {(hasChips || selectedStatuses.length > 0) && (
          <div className="mb-4 space-y-2">
            {hasChips && (
              <div className="flex items-center flex-wrap gap-2">
                {selectedDirections.map((dir) => (
                  <div
                    key={dir}
                    className="inline-flex items-center px-3 py-1 bg-dark-100 rounded-full text-sm"
                  >
                    <span className="text-dark-600 font-medium mr-1">
                      {dir}
                    </span>
                    <button
                      onClick={() => handleDirectionFilter(dir)}
                      className="ml-1 text-dark-400 hover:text-dark-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}

                {pickupLocation && (
                  <div className="inline-flex items-center px-3 py-1 bg-dark-100 rounded-full text-sm">
                    <span className="text-dark-500 mr-1">{t("list.pickup")}</span>
                    <span className="text-dark-600 font-medium mr-1">
                      {pickupLocation}
                    </span>
                    <button
                      onClick={() => handlePickupLocationChange("")}
                      className="ml-1 text-dark-400 hover:text-dark-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {deliveryLocation && (
                  <div className="inline-flex items-center px-3 py-1 bg-dark-100 rounded-full text-sm">
                    <span className="text-dark-500 mr-1">{t("list.delivery")}</span>
                    <span className="text-dark-600 font-medium mr-1">
                      {deliveryLocation}
                    </span>
                    <button
                      onClick={() => handleDeliveryLocationChange("")}
                      className="ml-1 text-dark-400 hover:text-dark-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}

                {selectedPartners.map((company) => (
                  <div
                    key={company._id}
                    className="inline-flex items-center px-3 py-1 bg-dark-100 rounded-full text-sm"
                  >
                    <span className="text-dark-600 font-medium mr-1">
                      {company.companyName}
                    </span>
                    <button
                      onClick={() => handlePartnerRemove(company._id)}
                      className="ml-1 text-dark-400 hover:text-dark-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}

                {selectedAddresses.map((company) => (
                  <div
                    key={company._id}
                    className="inline-flex items-center px-3 py-1 bg-dark-100 rounded-full text-sm"
                  >
                    <span className="text-dark-600 font-medium mr-1">
                      {company.companyName}
                    </span>
                    <button
                      onClick={() => handleAddressRemove(company._id)}
                      className="ml-1 text-dark-400 hover:text-dark-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}

                {truckCarrier.map((carrier, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-dark-100 rounded-full text-sm"
                  >
                    <span className="text-dark-600 font-medium mr-1">
                      {carrier}
                    </span>
                    <button
                      onClick={() => handleTruckRemove(carrier)}
                      className="ml-1 text-dark-400 hover:text-dark-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}

                {trailerCarrier.map((carrier, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center px-3 py-1 bg-dark-100 rounded-full text-sm"
                  >
                    <span className="text-dark-600 font-medium mr-1">
                      {carrier}
                    </span>
                    <button
                      onClick={() => handleTrailerRemove(carrier)}
                      className="ml-1 text-dark-400 hover:text-dark-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}

                {(dateRangeStart || dateRangeEnd) && (
                  <div className="inline-flex items-center px-3 py-1 bg-dark-100 rounded-full text-sm">
                    <span className="text-dark-600 font-medium mr-1">
                      {dateRangeStart && dateRangeEnd
                        ? `${new Date(dateRangeStart).toLocaleDateString()} – ${new Date(dateRangeEnd).toLocaleDateString()}`
                        : dateRangeStart
                          ? `${t("list.from")} ${new Date(dateRangeStart).toLocaleDateString()}`
                          : `${t("list.until")} ${new Date(dateRangeEnd).toLocaleDateString()}`}
                    </span>
                    <button
                      onClick={handleDateRangeClear}
                      className="ml-1 text-dark-400 hover:text-dark-600 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}

                <button
                  onClick={clearFilters}
                  className="text-sm text-dark-500 hover:text-dark-700 underline ml-2"
                >
                  {t("list.removeAllFilters")}
                </button>
              </div>
            )}

            {selectedStatuses.length > 0 && (
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-sm text-dark-600">{t("list.status")}</span>
                {selectedStatuses.map((status) => (
                  <span
                    key={status}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                  >
                    {status === "AwaitingPickup" ? t("list.pickedUp") : status}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Bulk selection bar */}
        {selectedIds.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800 font-medium">
                {t("list.itemsSelected", { count: selectedIds.length })}
              </span>
            </div>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              <span>{t("list.deleteSelected")}</span>
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-dark-200 overflow-hidden">
          <ShipmentTable
            shipments={shipments}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={loading}
            handleCopyShipment={handleCopyShipment}
            columns={reduxColumns}
            focusReclamation={focusReclamation}
            onFocusReclamationClose={() => setFocusReclamation(null)}
          />
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            pageSize={filters.limit}
          />
        </div>
      </div>
    </div>
  );
};
